/**
 * @description
 * Generic settings panel for key-value settings with inline editing
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import { useState } from 'react';
import { toast } from 'sonner';
import { api, type Setting } from '../../services/api';
import { GenericSettingsPanelProps } from './types';

/**
 * Generic settings panel component for simple key-value settings
 */
export function GenericSettingsPanel({ settings, category, onUpdate, user }: GenericSettingsPanelProps) {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');

  const handleEdit = (setting: Setting) => {
    setEditingKey(setting.key);
    setEditValue(setting.isSecret ? '' : setting.value);
  };

  const handleSave = async (key: string) => {
    try {
      await api.updateSetting(key, editValue, (user as { id: string })?.id);
      setEditingKey(null);
      setEditValue('');
      toast.success('Setting updated successfully');
      onUpdate(); // Refresh parent settings
    } catch (error) {
      console.error('Error updating setting:', error);
      toast.error('Failed to update setting');
    }
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const filteredSettings = settings.filter(s => {
    if (category === 'general') {
      // Show all settings that don't have a specific category or have 'general' category
      return !s.category || s.category === 'general';
    }
    return s.category === category;
  });

  return (
    <div className="space-y-4">
      {filteredSettings.map((setting) => {
        return (
          <div key={setting.key} className="border-b pb-4 last:border-0">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <label className="text-sm font-medium">{setting.key.replace(/_/g, ' ')}</label>
                {setting.description && (
                  <p className="text-xs text-muted-foreground mt-1">{setting.description}</p>
                )}
                {editingKey === setting.key ? (
                  <div className="mt-2 flex gap-2">
                    <input
                      type={setting.isSecret ? 'password' : 'text'}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      placeholder={setting.isSecret ? 'Enter new value' : setting.value}
                      className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <button
                      onClick={() => handleSave(setting.key)}
                      className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleCancel}
                      className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div className="mt-2 flex items-center gap-2">
                    <code className="flex-1 rounded bg-muted px-3 py-2 text-sm font-mono">
                      {setting.isSecret ? '••••••••' : setting.value}
                    </code>
                    <button
                      onClick={() => handleEdit(setting)}
                      className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
